import { useState, useEffect } from "react";
import { FaGoogle, FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  doSignInWithEmailAndPassword,
  doSignInWithGoogle,
  sendPasswordResetEmail,
  doSendPasswordResetEmail
} from "../firebase/auth"; // Import the sendPasswordResetEmail function
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RingLoader } from "react-spinners";



const LoginForm = ({ toggleForm, setShowPopup }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // 👀 Password visibility state
  const [emailPasswordLoading, setEmailPasswordLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false); // State to track if reset email is sent
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Redirect if user is already logged in
  useEffect(() => {
    if (currentUser) {
      navigate("/home");
    }
  }, [currentUser, navigate]);




  // Handle Email/Password Login
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isChecked) {
      setShowTermsError(true);
      return;
    }
    setEmailPasswordLoading(true);
    try {
      await doSignInWithEmailAndPassword(email, password);
      setEmail(""); // Clear email field
      setPassword(""); // Clear password field
      navigate("/home"); // Redirect after successful login
    } catch (error) {
      toast.error(error.message || "Invalid email or password", {
        toastId: "email-password-error",
      });
    }
    setEmailPasswordLoading(false);
  };

  // Handle Google Login
const handleGoogleLogin = async () => {
  if (!isChecked) {
    setShowTermsError(true);
    return;
  }

  setGoogleLoading(true);

  try {
    let userCredential = await doSignInWithGoogle();

    // ✅ If One Tap was dismissed, try again on next click
    if (!userCredential) {
      console.log("User dismissed Google login. Waiting for next click.");
      return;
    }
  } catch (error) {
    toast.error(error.message || "Google Login Failed", {
      toastId: "google-login-error",
    });
  } finally {
    setGoogleLoading(false);
  }
};


  // Handle Forgot Password
 const handleForgotPassword = async () => {
   if (!email) {
     toast.error("Please enter your email address to reset your password.");
     return;
   }

   try {
     await doSendPasswordResetEmail(email); // ✅ Use fixed function
     toast.success("Password reset email sent. Please check your inbox.");
   } catch (error) {
     toast.error(error.message || "Failed to send password reset email.");
   }
 };

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 p-6 tracking-tight rounded-xl duration-500">
      <h2 className="text-3xl font-semibold mb-4 text-center text-gray-900 dark:text-white">
        Login
      </h2>

      {/* Terms & Conditions Error Message */}
      {showTermsError && (
        <div className="mb-2 p-2 text-center text-red-600 bg-red-100 rounded-lg">
          You must agree to the Terms & Conditions to proceed.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Email Field */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Email
          </label>
          <div className="relative">
            <FaEnvelope className="absolute left-3 top-3 text-gray-500" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-10 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-300"
              placeholder="Enter your email"
              required
            />
          </div>
        </div>

        {/* Password Field with Eye Icon 👀 */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Password
          </label>
          <div className="relative">
            <FaLock className="absolute left-3 top-3 text-gray-500" />
            <input
              type={showPassword ? "text" : "password"} // Toggle input type
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-10 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-300"
              placeholder="Enter your password"
              required
            />
            {/* Toggle Password Visibility */}
            <button
              type="button"
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 rounded-full p-1"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        <div className="flex flex-auto justify-between items-center">
        {/* Terms & Conditions */}
        <div className="mb-6 flex items-center">
          <input
            type="checkbox"
            id="terms"
            className={`mr-2 ${isChecked ? "accent-blue-600" : ""}`}
            checked={isChecked}
            onChange={() => {
              setIsChecked(!isChecked);
              setShowTermsError(false);
            }}
          />
          <label htmlFor="terms" className="text-sm text-gray-700 dark:text-gray-300">
            I agree to the{" "}
            <span
              className="text-blue-600 underline cursor-pointer"
              onClick={() => setShowPopup(true)}
            >
              Terms & Conditions
            </span>
          </label>
        </div>

        {/* Forgot Password Button */}
        <div className="mb-6 text-right">
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-700 focus:outline-none"
            onClick={handleForgotPassword}
          >
            Forgot Password?
          </button>
        </div>

        </div>


        {/* Login Button */}
        <button
          type="submit"
          className="w-full py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={emailPasswordLoading}
        >
          {emailPasswordLoading ? (
            <div className="flex justify-center">
              <RingLoader color="#fff" loading={emailPasswordLoading} size={20} />
            </div>
          ) : (
            "Login"
          )}
        </button>
      </form>

      {/* OR Divider */}
      <div className="flex items-center my-2">
        <hr className="flex-grow border-gray-300" />
        <span className="px-2 text-gray-500">OR</span>
        <hr className="flex-grow border-gray-300" />
      </div>

      {/* Google Login Button */}
      <button
        className="w-full py-2 flex items-center justify-center border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleGoogleLogin}
        disabled={googleLoading}
      >
        <FaGoogle className="mr-2 text-red-500" />
        {googleLoading ? (
          <div className="flex justify-center">
            <RingLoader color="#fff" loading={googleLoading} size={20} />
          </div>
        ) : (
          "Login with Google"
        )}
      </button>

      {/* Switch to Register */}
      <p className="text-center text-sm mt-4 text-gray-600 dark:text-gray-300">
        Don't have an account?{" "}
        <button onClick={toggleForm} className="font-semibold text-blue-700 hover:text-blue-800">
          Register
        </button>
      </p>
    </div>
  );
};

export default LoginForm;
